import { create } from 'zustand';
import type { Coordinates, Place, PlaceCategory, SearchResult, SheetState, RecentSearch } from '../types';
import { getPlaceDetails, autocomplete, saveRecentSearch, type AutocompleteResult, APIError } from '../services/api';
import { trackEvent } from '../lib/posthog';
import { useAuthStore } from './authStore';
import { MAP_DEFAULT_ZOOM, MAP_FOCUS_ZOOM } from '../components/map/config/mapConfig';
import type { ContributionSheetSnap } from '../components/map/mapSheetOffset';
import { shouldAppFocusAdminPlace } from '../utils/adminBoundaryFocus';
import {
  outOfRegionMessage,
  pickGhanaProximity,
  regionAvailability,
  shouldWarnOnLocationFeature,
} from '../utils/regionAvailability';
import { isInGhana } from '../utils/geo';
import { slimRecentSearchResult } from '../utils/recentSearchStorage';

function focusPlaceOnMap(
  get: () => { flyToLocation: (coords: Coordinates, zoom?: number) => void },
  place: Pick<Place, 'category' | 'coordinates' | 'geometry' | 'placeType'>,
) {
  if (shouldAppFocusAdminPlace(place.placeType, place.geometry ?? undefined)) {
    // App.tsx highlights geometry and focuses the map (polygon fit or point fly).
    return;
  }
  const zoom =
    place.placeType === 'place' && place.category === 'country'
      ? 5
      : MAP_FOCUS_ZOOM;
  get().flyToLocation(place.coordinates, zoom);
}

// AbortController for cancelling stale autocomplete requests
let autocompleteAbortController: AbortController | null = null;
// Request counter to prevent race conditions with stale responses
let autocompleteRequestId = 0;

function haversineMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const rad = Math.PI / 180;
  const rlat1 = lat1 * rad;
  const rlat2 = lat2 * rad;
  const dlat = (lat2 - lat1) * rad;
  const dlng = (lon2 - lon1) * rad;
  const a = Math.sin(dlat / 2) ** 2 + Math.cos(rlat1) * Math.cos(rlat2) * Math.sin(dlng / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 10) / 10;
}

function handleAnonymousMapRateLimit(
  set: (partial: Partial<MapStore> | ((state: MapStore) => Partial<MapStore>)) => void,
  error: APIError,
) {
  trackEvent.rateLimitHit();
  set({ searchError: error.getUserMessage(), isSearching: false, isLoadingMore: false });
  const { isAuthenticated, openAuthModal } = useAuthStore.getState();
  if (!isAuthenticated) {
    openAuthModal('login');
  }
}

function fillDistances(suggestions: AutocompleteResult[], userLoc: { lat: number; lng: number } | null): AutocompleteResult[] {
  if (!userLoc) return suggestions;
  return suggestions.map((s) => {
    if (s.distance_meters != null) return s;
    if (!s.coordinates?.latitude || !s.coordinates?.longitude) return s;
    return {
      ...s,
      distance_meters: haversineMeters(userLoc.lat, userLoc.lng, s.coordinates.latitude, s.coordinates.longitude),
    };
  });
}

/** Session-only recent searches for signed-in users; sync to account when searchHistory is enabled. */
function recordRecentSearch(
  set: (fn: (state: { recentSearches: RecentSearch[] }) => { recentSearches: RecentSearch[] }) => void,
  recent: RecentSearch,
  dedupe: (existing: RecentSearch) => boolean,
  sync?: {
    query: string;
    result_id?: string;
    result_name?: string;
    result_address?: string;
    result_opening_hours_status?: string;
  },
) {
  if (!useAuthStore.getState().isAuthenticated) return;

  set((state) => ({
    recentSearches: [recent, ...state.recentSearches.filter((r) => !dedupe(r)).slice(0, 4)],
  }));
  if (sync) {
    void import('./settingsStore').then(({ useSettingsStore }) => {
      if (useSettingsStore.getState().settings.searchHistory) {
        saveRecentSearch(sync).catch(() => { });
      }
    });
  }
}

interface MapStore {
  // Map state
  center: Coordinates | null;
  zoom: number;
  isMapLoaded: boolean;

  // Search state
  searchQuery: string;
  searchType: string;
  searchResults: SearchResult[];
  autocompleteSuggestions: AutocompleteResult[];
  autocompleteHasMore: boolean;
  autocompleteOffset: number;
  autocompleteTotal: number;
  isLoadingMore: boolean;
  isSearching: boolean;
  recentSearches: RecentSearch[];
  showSearchResults: boolean;
  searchError: string | null;

  // Place state
  selectedPlace: Place | null;

  // Sheet state
  sheetState: SheetState;
  /** Contribution panel snap — drives map chrome offset while Add Location is open. */
  contributionSheetSnap: ContributionSheetSnap | null;
  /** Measured gap from viewport bottom to top of contribution sheet (+ padding). */
  contributionSheetChromeBottom: string | null;

  // User location
  userLocation: Coordinates | null;
  isLocating: boolean;

  /** Shown when search/directions/transport is used outside Ghana service area. */
  outOfRegionMessage: string | null;
  outOfRegionDismissed: boolean;

  /** Bumped on each intentional flyToLocation — map listens to this, not every center change. */
  flyRequestId: number;

  // Actions
  setCenter: (center: Coordinates) => void;
  setZoom: (zoom: number) => void;
  setMapLoaded: (loaded: boolean) => void;

  setSearchQuery: (query: string) => void;
  setSearchType: (type: string) => void;
  performSearch: (query: string) => void;
  performAutocomplete: (query: string) => void;
  loadMoreAutocomplete: () => void;
  clearSearch: () => void;
  setShowSearchResults: (show: boolean) => void;

  selectPlace: (placeId: string | null) => void;
  selectAutocompleteResult: (result: AutocompleteResult) => void;
  setSelectedPlaceFromData: (place: Place, options?: { fly?: boolean }) => void;
  clearSelectedPlace: () => void;

  setSheetState: (state: SheetState) => void;
  setContributionSheetSnap: (snap: ContributionSheetSnap | null) => void;
  setContributionSheetChromeBottom: (bottom: string | null) => void;

  setUserLocation: (location: Coordinates | null) => void;
  setIsLocating: (locating: boolean) => void;
  warnOnLocationFeatureUse: () => void;
  dismissOutOfRegionBanner: () => void;

  flyToLocation: (coords: Coordinates, zoom?: number) => void;

  /** Pin shown when a transport stop is tapped (distinct from selectedPlace search pin). */
  selectedStopCoords: { lat: number; lng: number; name: string } | null;
  setSelectedStopCoords: (coords: { lat: number; lng: number; name: string } | null) => void;
}

export const useMapStore = create<MapStore>()((set, get) => ({
    center: null,
    zoom: MAP_DEFAULT_ZOOM,
    isMapLoaded: false,

    searchQuery: '',
    searchType: 'all',
    searchResults: [],
    autocompleteSuggestions: [],
    autocompleteHasMore: false,
    autocompleteOffset: 0,
    autocompleteTotal: 0,
    isLoadingMore: false,
    isSearching: false,
    recentSearches: [], // Session-only; hydrated from API after sign-in when searchHistory is enabled
    showSearchResults: false,
    searchError: null,

    selectedPlace: null,

    sheetState: 'half',
    contributionSheetSnap: null,
    contributionSheetChromeBottom: null,

    userLocation: null,
    isLocating: false,
    outOfRegionMessage: null,
    outOfRegionDismissed: false,
    flyRequestId: 0,

    // Actions
    setCenter: (center) => set({ center }),
    setZoom: (zoom) => set({ zoom }),
    setMapLoaded: (loaded) => set({ isMapLoaded: loaded }),

    setSearchQuery: (query) => set({ searchQuery: query }),
    setSearchType: (type) => set({ searchType: type }),

    warnOnLocationFeatureUse: () => {
      const { userLocation, center, outOfRegionDismissed } = get();
      if (outOfRegionDismissed) return;
      const mode = regionAvailability(userLocation, center);
      if (mode === 'in_ghana') {
        set({ outOfRegionMessage: null });
        return;
      }
      if (!shouldWarnOnLocationFeature(userLocation, center)) return;
      const message = outOfRegionMessage(mode);
      if (message) set({ outOfRegionMessage: message });
    },

    dismissOutOfRegionBanner: () => set({ outOfRegionMessage: null, outOfRegionDismissed: true }),

    performAutocomplete: async (query) => {
      if (query.length < 2) {
        set({ autocompleteSuggestions: [], autocompleteHasMore: false, autocompleteOffset: 0, autocompleteTotal: 0, isSearching: false });
        return;
      }

      // Cancel any pending autocomplete request
      if (autocompleteAbortController) {
        autocompleteAbortController.abort();
      }
      autocompleteAbortController = new AbortController();

      // Increment request ID to track this specific request
      autocompleteRequestId++;
      const thisRequestId = autocompleteRequestId;

      set({ isSearching: true, autocompleteOffset: 0, autocompleteHasMore: false });
      get().warnOnLocationFeatureUse();

      try {
        const userLocation = get().userLocation;
        const searchType = get().searchType;
        const proximity = pickGhanaProximity(userLocation, get().center);
        const result = await autocomplete(query, {
          limit: 8,
          offset: 0,
          proximity,
          searchType,
        });

        if (thisRequestId === autocompleteRequestId) {
          const withDist = fillDistances(result.suggestions, get().userLocation);
          set({
            autocompleteSuggestions: withDist,
            autocompleteHasMore: result.has_more,
            autocompleteOffset: result.suggestions.length,
            autocompleteTotal: result.total,
            isSearching: false,
          });
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        console.error('Autocomplete failed:', error);
        if (thisRequestId === autocompleteRequestId) {
          if (error instanceof APIError && error.isRateLimitError()) {
            handleAnonymousMapRateLimit(set, error);
            set({ autocompleteSuggestions: [], autocompleteHasMore: false });
          } else {
            trackEvent.searchError('autocomplete_failed');
            set({ autocompleteSuggestions: [], autocompleteHasMore: false, isSearching: false, searchError: null });
          }
        }
      }
    },

    loadMoreAutocomplete: async () => {
      const { searchQuery, autocompleteOffset, isLoadingMore, autocompleteSuggestions, searchType } = get();
      if (isLoadingMore || !searchQuery) return;

      set({ isLoadingMore: true });
      try {
        const userLocation = get().userLocation;
        const proximity = pickGhanaProximity(userLocation, get().center);
        const result = await autocomplete(searchQuery, {
          limit: 8,
          offset: autocompleteOffset,
          proximity,
          searchType,
        });
        const newWithDist = fillDistances(result.suggestions, get().userLocation);
        set({
          autocompleteSuggestions: [...autocompleteSuggestions, ...newWithDist],
          autocompleteHasMore: result.has_more,
          autocompleteOffset: autocompleteOffset + result.suggestions.length,
          autocompleteTotal: result.total,
          isLoadingMore: false,
        });
      } catch (error) {
        if (error instanceof APIError && error.isRateLimitError()) {
          handleAnonymousMapRateLimit(set, error);
        }
        set({ isLoadingMore: false });
      }
    },

    selectAutocompleteResult: async (result) => {
      const hasCoords =
        result.coordinates &&
        result.coordinates.latitude != null &&
        result.coordinates.longitude != null;

      const needsPlaceLookup = !hasCoords && Boolean(result.id);

      const isStreet = result.type === 'street';

      const initialPlace: Place = {
        id: result.id || `place_${Date.now()}`,
        name: result.text,
        placeType: result.type,
        category: (isStreet ? 'road' : (result.category || result.type)) as PlaceCategory,
        address: result.address || result.text,
        district: result.district ?? undefined,
        coordinates: hasCoords
          ? { lat: result.coordinates!.latitude, lng: result.coordinates!.longitude }
          : { lat: 0, lng: 0 },
        ghanaPostCode: result.postcode,
        floodRiskLevel: result.flood_risk_level ?? undefined,
        floodProne: result.flood_prone ?? undefined,
        avgRainfallMm: result.avg_rainfall_mm ?? undefined,
        seasonalAccessNotes: result.seasonal_access_notes ?? undefined,
      };

      set({
        selectedPlace: initialPlace,
        selectedStopCoords: null,
        searchQuery: result.text,
        autocompleteSuggestions: [],
        showSearchResults: false,
        sheetState: 'half',
      });

      const applyPlaceDetails = (apiPlace: Awaited<ReturnType<typeof getPlaceDetails>>) => {
        const placeWithGeometry: Place = {
          id: apiPlace.id,
          name: apiPlace.name,
          placeType: apiPlace.type,
          category: (apiPlace.type === 'street' ? 'road' : (apiPlace.category || apiPlace.type)) as PlaceCategory,
          address: apiPlace.formatted_address,
          streetAddress: apiPlace.street_address,
          houseNumber: apiPlace.house_number,
          frontageStreetAddress: apiPlace.frontage_street_address,
          addrStreet: apiPlace.addr_street,
          addrSuburb: apiPlace.addr_suburb,
          addrCity: apiPlace.addr_city,
          district: apiPlace.district,
          region: apiPlace.region,
          coordinates: { lat: apiPlace.coordinates.latitude, lng: apiPlace.coordinates.longitude },
          geometry: apiPlace.geometry,
          phone: apiPlace.phone,
          mobile: apiPlace.mobile,
          whatsapp: apiPlace.whatsapp,
          email: apiPlace.email,
          website: apiPlace.website,
          facebook: apiPlace.facebook,
          instagram: apiPlace.instagram,
          rating: apiPlace.rating,
          reviewCount: apiPlace.reviewCount || apiPlace.review_count,
          hours: apiPlace.hours ? {
            open: apiPlace.hours.open,
            close: apiPlace.hours.close,
            isOpen: apiPlace.hours.is_open,
          } : undefined,
          openingHoursStatus: apiPlace.opening_hours_status ?? undefined,
          openingHours: apiPlace.opening_hours ?? undefined,
          description: apiPlace.description,
          descriptionSourceUrl: apiPlace.description_source_url,
          ghanaPostCode: apiPlace.ghana_post_code,
          ghanaPostGPS: apiPlace.ghana_post_gps,
          gssPcode: apiPlace.gss_pcode,
          adminSource: apiPlace.admin_source,
          adminLevel: apiPlace.admin_level,
          brand: apiPlace.brand,
          cuisine: apiPlace.cuisine,
          amenities: apiPlace.amenities ? {
            wheelchair: apiPlace.amenities.wheelchair,
            internetAccess: apiPlace.amenities.internet_access,
            airConditioning: apiPlace.amenities.air_conditioning,
            outdoorSeating: apiPlace.amenities.outdoor_seating,
            delivery: apiPlace.amenities.delivery,
            takeaway: apiPlace.amenities.takeaway,
            parking: apiPlace.amenities.parking,
            driveThrough: apiPlace.amenities.drive_through,
          } : undefined,
          buildingUnits: apiPlace.building_units?.map((u) => ({ label: u.label, floor: u.floor })),
          buildingLevels: apiPlace.building_levels,
          stopRef: apiPlace.stop_ref ?? undefined,
          routes: apiPlace.routes?.length ? apiPlace.routes : undefined,
          operators: apiPlace.operators?.length ? apiPlace.operators : undefined,
          shelter: apiPlace.shelter ?? undefined,
          bench: apiPlace.bench ?? undefined,
          lighting: apiPlace.lighting ?? undefined,
          floodRiskLevel: apiPlace.flood_risk_level ?? undefined,
          floodProne: apiPlace.flood_prone ?? undefined,
          avgRainfallMm: apiPlace.avg_rainfall_mm ?? undefined,
          seasonalAccessNotes: apiPlace.seasonal_access_notes ?? undefined,
        };
        set({ selectedPlace: placeWithGeometry });
        return placeWithGeometry;
      };

      if (needsPlaceLookup) {
        try {
          const apiPlace = await getPlaceDetails(result.id!);
          const placeWithGeometry = applyPlaceDetails(apiPlace);
          focusPlaceOnMap(get, placeWithGeometry);
          recordRecentSearch(
            set,
            {
              id: `recent_${Date.now()}`,
              query: result.text,
              timestamp: Date.now(),
              result: slimRecentSearchResult({
                id: placeWithGeometry.id,
                name: placeWithGeometry.name,
                category: placeWithGeometry.category,
                type: placeWithGeometry.placeType,
              }),
            },
            (r) => r.result?.id === placeWithGeometry.id,
            {
              query: result.text,
              result_id: result.id,
              result_name: result.text,
              result_address: result.address ?? undefined,
              result_opening_hours_status: placeWithGeometry.openingHoursStatus ?? undefined,
            },
          );
          return;
        } catch (error) {
          console.error('Failed to fetch place details:', error);
        }
      }

      if (hasCoords) {
        get().flyToLocation(initialPlace.coordinates, MAP_FOCUS_ZOOM);
      }

      recordRecentSearch(
        set,
        {
          id: `recent_${Date.now()}`,
          query: result.text,
          timestamp: Date.now(),
          result: slimRecentSearchResult({
            id: initialPlace.id,
            name: initialPlace.name,
            category: initialPlace.category,
            type: initialPlace.placeType,
          }),
        },
        (r) => r.result?.id === initialPlace.id,
        {
          query: result.text,
          result_id: result.id,
          result_name: result.text,
          result_address: result.address ?? undefined,
          result_opening_hours_status: result.opening_hours_status ?? undefined,
        },
      );

      if (result.id && hasCoords) {
        try {
          const apiPlace = await getPlaceDetails(result.id);
          const placeWithGeometry = applyPlaceDetails(apiPlace);
          // Streets: keep search pin on the matched line (place details is per-segment).
          if (isStreet) {
            set({
              selectedPlace: {
                ...placeWithGeometry,
                coordinates: initialPlace.coordinates,
              },
            });
          }
        } catch (error) {
          console.error('Failed to fetch place details for geometry:', error);
        }
      }
    },

    performSearch: async (query) => {
      set({ isSearching: true, showSearchResults: true, autocompleteSuggestions: [], searchResults: [], autocompleteOffset: 0, autocompleteHasMore: false });
      get().warnOnLocationFeatureUse();

      try {
        const searchType = get().searchType;
        const proximity = pickGhanaProximity(get().userLocation, get().center);

        const result = await autocomplete(query, { limit: 15, offset: 0, proximity, searchType });
        const suggestionsWithDist = fillDistances(result.suggestions, get().userLocation);

        const _typeMap: Record<string, SearchResult['type']> = { boundary: 'place', postcode: 'address', frontage: 'address' };
        const results: SearchResult[] = suggestionsWithDist
          .filter((s) => s.coordinates?.latitude != null && s.coordinates?.longitude != null)
          .map((s) => ({
          id: s.id,
          name: s.text,
          type: (_typeMap[s.type] ?? s.type) as SearchResult['type'],
          category: s.category || s.type,
          address: s.address ?? undefined,
          district: s.district ?? undefined,
          postcode: s.postcode,
          postcode_display: s.postcode_display,
          ghana_post: s.ghana_post,
          opening_hours_status: s.opening_hours_status ?? undefined,
          coordinates: { lat: s.coordinates!.latitude, lng: s.coordinates!.longitude },
          distance: s.distance_meters ? s.distance_meters / 1000 : undefined,
          distance_meters: s.distance_meters,
          rating: undefined,
          flood_risk_level: s.flood_risk_level ?? null,
          flood_prone: s.flood_prone ?? null,
          avg_rainfall_mm: s.avg_rainfall_mm ?? null,
          seasonal_access_notes: s.seasonal_access_notes ?? null,
        }));

        trackEvent.searchPerformed(query, results.length);

        set({
          searchResults: results,
          isSearching: false,
          showSearchResults: true,
          autocompleteHasMore: result.has_more,
          autocompleteOffset: result.suggestions.length,
          autocompleteTotal: result.total,
        });

        if (results.length > 0 && query.trim()) {
          recordRecentSearch(
            set,
            {
              id: `recent_${Date.now()}`,
              query: query,
              timestamp: Date.now(),
              result: slimRecentSearchResult(results[0]),
            },
            () => false,
            {
              query: query,
              result_id: results[0].id,
              result_name: results[0].name,
              result_address: results[0].address,
              result_opening_hours_status: results[0].opening_hours_status,
            },
          );
        }
      } catch (error) {
        console.error('Search failed:', error);
        if (error instanceof APIError && error.isRateLimitError()) {
          handleAnonymousMapRateLimit(set, error);
          set({ searchResults: [] });
        } else {
          trackEvent.searchError('search_failed');
          set({ searchResults: [], isSearching: false, searchError: null });
        }
      }
    },

    clearSearch: () => set({
      searchQuery: '',
      searchResults: [],
      autocompleteSuggestions: [],
      autocompleteHasMore: false,
      autocompleteOffset: 0,
      autocompleteTotal: 0,
      showSearchResults: false,
      sheetState: 'half',
      searchError: null
    }),

    setShowSearchResults: (show) => set({ showSearchResults: show }),

    selectPlace: async (placeId) => {
      if (!placeId) {
        set({ selectedPlace: null, sheetState: 'collapsed' });
        return;
      }

      try {
        // Fetch place details from API
        const apiPlace = await getPlaceDetails(placeId);

        // Transform to our Place format
        const place: Place = {
          id: apiPlace.id,
          name: apiPlace.name,
          placeType: apiPlace.type,
          category: (apiPlace.type === 'street' ? 'road' : (apiPlace.category || apiPlace.type)) as PlaceCategory,
          address: apiPlace.formatted_address,
          streetAddress: apiPlace.street_address,
          houseNumber: apiPlace.house_number,
          frontageStreetAddress: apiPlace.frontage_street_address,
          addrStreet: apiPlace.addr_street,
          addrSuburb: apiPlace.addr_suburb,
          addrCity: apiPlace.addr_city,
          district: apiPlace.district,
          region: apiPlace.region,
          coordinates: { lat: apiPlace.coordinates.latitude, lng: apiPlace.coordinates.longitude },
          geometry: apiPlace.geometry,  // Building polygon for highlighting
          phone: apiPlace.phone,
          mobile: apiPlace.mobile,
          whatsapp: apiPlace.whatsapp,
          email: apiPlace.email,
          website: apiPlace.website,
          facebook: apiPlace.facebook,
          instagram: apiPlace.instagram,
          rating: apiPlace.rating,
          reviewCount: apiPlace.reviewCount || apiPlace.review_count,
          hours: apiPlace.hours ? {
            open: apiPlace.hours.open,
            close: apiPlace.hours.close,
            isOpen: apiPlace.hours.is_open,
          } : undefined,
          openingHoursStatus: apiPlace.opening_hours_status ?? undefined,
          openingHours: apiPlace.opening_hours ?? undefined,
          description: apiPlace.description,
          descriptionSourceUrl: apiPlace.description_source_url,
          ghanaPostCode: apiPlace.ghana_post_code,
          ghanaPostGPS: apiPlace.ghana_post_gps,
          gssPcode: apiPlace.gss_pcode,
          adminSource: apiPlace.admin_source,
          adminLevel: apiPlace.admin_level,
          brand: apiPlace.brand,
          cuisine: apiPlace.cuisine,
          amenities: apiPlace.amenities ? {
            wheelchair: apiPlace.amenities.wheelchair,
            internetAccess: apiPlace.amenities.internet_access,
            airConditioning: apiPlace.amenities.air_conditioning,
            outdoorSeating: apiPlace.amenities.outdoor_seating,
            delivery: apiPlace.amenities.delivery,
            takeaway: apiPlace.amenities.takeaway,
            parking: apiPlace.amenities.parking,
            driveThrough: apiPlace.amenities.drive_through,
          } : undefined,
          buildingUnits: apiPlace.building_units?.map((u) => ({ label: u.label, floor: u.floor })),
          buildingLevels: apiPlace.building_levels,
          stopRef: apiPlace.stop_ref ?? undefined,
          routes: apiPlace.routes?.length ? apiPlace.routes : undefined,
          operators: apiPlace.operators?.length ? apiPlace.operators : undefined,
          shelter: apiPlace.shelter ?? undefined,
          bench: apiPlace.bench ?? undefined,
          lighting: apiPlace.lighting ?? undefined,
          floodRiskLevel: apiPlace.flood_risk_level ?? undefined,
          floodProne: apiPlace.flood_prone ?? undefined,
          avgRainfallMm: apiPlace.avg_rainfall_mm ?? undefined,
          seasonalAccessNotes: apiPlace.seasonal_access_notes ?? undefined,
        };

        set({
          selectedPlace: place,
          selectedStopCoords: null,
          sheetState: 'half',
          showSearchResults: false
        });

        recordRecentSearch(
          set,
          {
            id: `recent_${Date.now()}`,
            query: place.name,
            timestamp: Date.now(),
            result: slimRecentSearchResult({
              id: place.id,
              name: place.name,
              category: place.category,
              type: place.placeType,
            }),
          },
          (r) => r.result?.id === place.id,
          {
            query: place.name,
            result_id: place.id,
            result_name: place.name,
            result_address: place.address,
            result_opening_hours_status: place.openingHoursStatus ?? undefined,
          },
        );

        // Fly to place (admin boundaries use fitBounds in App.tsx)
        focusPlaceOnMap(get, place);
      } catch (error) {
        console.error('Failed to get place details:', error);
        if (error instanceof APIError && error.isRateLimitError()) {
          handleAnonymousMapRateLimit(set, error);
          return;
        }
        // Fallback: use in-session search results only (recent entries omit coordinates).
        const { searchResults } = get();
        const result = searchResults.find((r) => r.id === placeId);
        if (result) {
          const place: Place = {
            id: result.id,
            name: result.name,
            category: result.category,
            address: result.address || '',
            coordinates: result.coordinates,
            rating: result.rating,
            placeType: result.type,
            floodRiskLevel: result.flood_risk_level ?? undefined,
            floodProne: result.flood_prone ?? undefined,
            avgRainfallMm: result.avg_rainfall_mm ?? undefined,
            seasonalAccessNotes: result.seasonal_access_notes ?? undefined,
          };
          set({
            selectedPlace: place,
            sheetState: 'half',
            showSearchResults: false
          });

          recordRecentSearch(
            set,
            {
              id: `recent_${Date.now()}`,
              query: place.name,
              timestamp: Date.now(),
              result: slimRecentSearchResult(result),
            },
            (r) => r.result?.id === place.id,
            {
              query: place.name,
              result_id: place.id,
              result_name: place.name,
              result_address: place.address,
            },
          );

          get().flyToLocation(place.coordinates, MAP_FOCUS_ZOOM);
        }
      }
    },

    setSelectedPlaceFromData: (place, options) => {
      set({
        selectedPlace: place,
        selectedStopCoords: null,
        sheetState: 'half',
        showSearchResults: false,
      });
      if (options?.fly !== false) {
        get().flyToLocation(place.coordinates, MAP_FOCUS_ZOOM);
      }
    },

    clearSelectedPlace: () => set({
      selectedPlace: null,
      sheetState: 'half'
    }),

    setSheetState: (state) => set({ sheetState: state }),

    setContributionSheetSnap: (snap) => set({ contributionSheetSnap: snap }),

    setContributionSheetChromeBottom: (bottom) => set({ contributionSheetChromeBottom: bottom }),

    setUserLocation: (location) => {
      if (location && isInGhana(location.lng, location.lat)) {
        set({ userLocation: location, outOfRegionMessage: null });
        return;
      }
      set({ userLocation: location });
    },
    setIsLocating: (locating) => set({ isLocating: locating }),

    selectedStopCoords: null,
    setSelectedStopCoords: (coords) => {
      if (coords) {
        set({ selectedStopCoords: coords, selectedPlace: null });
      } else {
        set({ selectedStopCoords: null });
      }
    },

    flyToLocation: (coords, zoom = MAP_DEFAULT_ZOOM) => {
      set((state) => ({
        center: coords,
        zoom,
        flyRequestId: state.flyRequestId + 1,
      }));
    },

}));

if (import.meta.env.VITE_PLAYWRIGHT_TEST === 'true' && typeof window !== 'undefined') {
  (window as unknown as { __coordalMapStore: typeof useMapStore }).__coordalMapStore = useMapStore;
}
